import {Component, Inject, OnInit} from '@angular/core';
import {UntypedFormControl, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {User} from '@nh/angular-tses';
import {DepartureCommentType} from 'app/comment/departure-comment-type';
import {EnvironmentVariableService} from 'app/core/services/environment-variable.service';
import {ReasonCodeService} from 'app/diary/reason-codes/reason-codes.service';
import {Reassignment, RoleService} from '../../../core/services/role.service';
import {FormValidator} from '../../../core/validation/form-validator';
import {DiaryService} from '../../../diary/diary.service';
import {DepartureProperty} from '../../../diary/properties/departure-properties';
import {DepartureRole} from '../../../permissions/types';
import {BaseTransitionModalComponent} from '../base-transition-modal/base-transition-modal.component';
import {TransitionInputData} from '../transition-input-data';
import {TransitionOutputData} from '../transition-output-data';
import {AssignableRole} from '../user-assign-modal/user-assign-modal.component';

@Component({
  selector: 'das-proposer-submit-modal',
  templateUrl: './proposer-submit-modal.component.html',
  styleUrls: [
    './proposer-submit-modal.component.scss',
    '../base-transition-modal/base-transition-modal.component.scss',
  ],
})
export class ProposerSubmitModalComponent extends BaseTransitionModalComponent implements OnInit {
  protected reassignments: Reassignment[] = [];
  public todayDate: string;

  public inputJobTitle: UntypedFormControl;
  public inputOrganisation: UntypedFormControl;
  public inputDesignersName: UntypedFormControl;

  constructor(
    public dialogRef: MatDialogRef<ProposerSubmitModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: TransitionInputData,
    roleService: RoleService,
    diaryService: DiaryService,
    reasonCodeService: ReasonCodeService,
    environmentVariableService: EnvironmentVariableService
  ) {
    super(dialogRef, data, roleService, diaryService, reasonCodeService, environmentVariableService);
    this.todayDate = this.getTodayDateFormatted();
  }

  protected setupFormControls(): void {
    this.inputComment = new UntypedFormControl(
      '',
      this.data.commentRequired ? [Validators.required, FormValidator.whitespace] : [FormValidator.whitespace]
    );
    this.inputJobTitle = new UntypedFormControl('', [Validators.required, FormValidator.whitespace]);
    this.inputOrganisation = new UntypedFormControl('', [Validators.required, FormValidator.whitespace]);
    this.inputDesignersName = new UntypedFormControl('', [Validators.required, FormValidator.whitespace]);
  }

  public ngOnInit(): void {
    super.ngOnInit();
    this.setupFormControls();
  }

  public actionButtonText(): string {
    return this.data && this.data.actionButton ? this.data.actionButton : 'ASSIGN';
  }

  public assign(): void {
    if (!this.checkFormValidity()) {
      return;
    }

    const returnData: TransitionOutputData = {
      cancelled: false,
      snapShot: super.generateDiarySnapshot(
        this.inputComment.value + '\n' + this.getFilledDeclarationComment(),
        DepartureCommentType.COMMENT,
        this.reassignments.splice(0),
        this.generatePropertiesRecord()
      ),
    };

    this.dialogRef.close(returnData);
  }

  public checkFormValidity(): boolean {
    return (
      !!this.reassignments.find((r) => r.role === DepartureRole.PROJECT_MANAGER)?.user &&
      this.inputComment.valid &&
      this.inputJobTitle.valid &&
      this.inputOrganisation.valid &&
      this.inputDesignersName.valid
    );
  }

  public cancel(): void {
    super.cancel();
  }

  public selected(user: User, assignment: AssignableRole): void {
    this.reassignments = this.reassignments.filter((currentReassignment: Reassignment) => {
      return currentReassignment.role !== assignment.role;
    });
    this.reassignments.push({
      role: assignment.role,
      user: user,
    });
  }

  private getTodayDateFormatted(): string {
    const today = new Date();
    const year = today.getFullYear();
    let mm = today.getMonth() + 1;
    let dd = today.getDate();

    let date: string;
    let month: string;

    if (dd < 10) date = '0' + dd;
    else date = dd.toString();
    if (mm < 10) month = '0' + mm;
    else month = mm.toString();

    return date + '/' + month + '/' + year;
  }

  private getFilledDeclarationComment(): string {
    return (
      " \nDesign Organisation's Declaration\n\n" +
      ' We confirm on behalf of ' +
      this.inputOrganisation.value +
      ' that this application for a Departure from Standards has been assessed and that:\n' +
      '\n' +
      '  In our professional view, the proposed Departure from Standard is fully justified in of the above-mentioned assessment;\n' +
      '\n' +
      '  The residual risks are so far as is reasonably practicable (SFAIRP) and the design with the departure has a net benefit when compared to a fully compliant design;\n' +
      '\n' +
      '  On the basis of the above, we recommend approval of the attached application for a Departure from Standard;\n' +
      '\n' +
      '  We confirm that we have used reasonable professional skill and care in the preparation of the design (encompassing the Departure from Standard);\n' +
      '\n' +
      '  We confirm our understanding that approval of this application, if granted, is on the basis that the safety risk treatments and control measures listed in the application are to be incorporated into the design.\n' +
      '\n' +
      "  Designer's Name: " +
      this.inputDesignersName.value +
      '\n' +
      '  Job Title: ' +
      this.inputJobTitle.value +
      '\n' +
      '  Date: ' +
      this.todayDate
    );
  }

  private generatePropertiesRecord(): Record<string, string | number | boolean> {
    const propertiesRecord: Record<string, string | number | boolean> = {};

    propertiesRecord[DepartureProperty.PROPOSER_DECLARATION] = true;

    return propertiesRecord;
  }
}
